import * as React from "react";
import { useEffect, useState, useRef } from "react";
import '../../../public/css/sign-in-form.css';
import '../../../public/css/google-sign-in.css';
import { InputFieldState } from "./InputFieldState";
import EmailInput from "./EmailInput";
import PasswordInput from "./PasswordInput";
import ButtonBig from "./ButtonBig";
import { fetch_post } from "../helpers/Fetch";
import { LoaderSpinner } from "./LoaderSpinner";
import { CheckBox } from "./CheckBox";
import PasskeyButton from "./PasskeyButton";
import { authenticateWithPasskey, isWebAuthnSupported, registerPasskey } from "../helpers/webauthn";
import TFACodeInput from "./TFACodeInput";

interface SignInFormProps {
  config: {
    signInUrl: string,
    redirect: boolean,
    redirectUrl?: string,
    googleAuthUrl: string,
    popupGoogle?: boolean,
    tfaUrl: string,
    setTimezoneUrl?: string,
    timezoneOffset?: number,
    keepLoggedIn: boolean,
    keepLoggedInUrl: string,
    passkeyAuthOptionsUrl?: string,
    passkeyAuthVerifyUrl?: string,
    passkeyCheckUrl?: string,
    passkeyRegisterOptionsUrl?: string,
    passkeyRegisterVerifyUrl?: string,
    passwordForgottenUrl?: string,
    tfaVerifyUrl?: string,
  },
  translations: {
    yourEmailAddress: string,
    yourPassword: string,
    login: string,
    continue?: string,
    or: string,
    keepLoggedInCheckbox: string,
    signInWithPasskey?: string,
    signInWithPassword?: string,
    back?: string,
    useYourAccount?: string,
    chooseLoginMethod?: string,
    passwordForgotten?: string,
    tfaVerification?: string,
    tfaEnterCode?: string,
    tfaHelperText?: string,
    verify?: string,
  }
  onLoginSuccess?: () => void,
}

// Login flow steps
type LoginStep = 'email' | 'passkey' | 'password' | 'passkeyEnrollment' | 'tfa';

const SignInForm: React.FC<SignInFormProps> = ({ config, translations, onLoginSuccess }) => {
  const [email, setEmail] = useState<InputFieldState>({ value: '' });
  const [password, setPassword] = useState<InputFieldState>({ value: '' });
  const [tfaCode, setTfaCode] = useState<InputFieldState>({ value: '' });
  const [timezoneOffset, setTimezoneOffset] = useState<number>(new Date().getTimezoneOffset());
  const [googleSignInDisabled, setGoogleSignInDisabled] = useState<boolean>(false);
  const [keepMeLoggedIn, setKeepMeLoggedIn] = useState<InputFieldState>({ value: '0', isChecked: false });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [passkeySupported, setPasskeySupported] = useState<boolean>(false);
  const [passkeyLoading, setPasskeyLoading] = useState<boolean>(false);
  const [passkeyError, setPasskeyError] = useState<string | null>(null);

  // Progressive login flow state
  const [currentStep, setCurrentStep] = useState<LoginStep>('email');
  const [previousStep, setPreviousStep] = useState<LoginStep | null>(null);
  const [hasPasskey, setHasPasskey] = useState<boolean>(false);
  const [checkingPasskey, setCheckingPasskey] = useState<boolean>(false);
  const [slideDirection, setSlideDirection] = useState<'forward' | 'backward'>('forward');
  const [enrollmentLoading, setEnrollmentLoading] = useState<boolean>(false);

  useEffect(() => {
    if (config.setTimezoneUrl && config.timezoneOffset === null) {
      submitTimezoneOffset();
    }

    // Check if passkey is supported
    setPasskeySupported(isWebAuthnSupported() && !!config.passkeyAuthOptionsUrl && !!config.passkeyAuthVerifyUrl);

    const lastEmail = localStorage.getItem('last_login_email');
    if (lastEmail) {
      setEmail({ value: lastEmail });
    }
  }, []);

  const submitTimezoneOffset = () => {
    fetch_post(config.setTimezoneUrl, {
      offset: timezoneOffset,
    }).then(res => res.json()).then((data: { success: boolean }) => {
      if (data.success) {
        // Handle success if needed
      }
    });
  };

  // Transition to next step with slide animation
  const goToStep = (step: LoginStep, direction: 'forward' | 'backward' = 'forward') => {
    setSlideDirection(direction);
    setPreviousStep(currentStep);
    setCurrentStep(step);
  };

  // Check if email has passkey registered
  const checkPasskeyForEmail = async () => {
    if (!config.passkeyCheckUrl || !passkeySupported || !email.value) {
      return false;
    }

    try {
      setCheckingPasskey(true);
      const response = await fetch_post(config.passkeyCheckUrl, { email: email.value });
      const data = await response.json();
      return data.success && data.hasPasskey;
    } catch (e) {
      return false;
    } finally {
      setCheckingPasskey(false);
    }
  };

  // Handle continue button - check for passkey and move to next step
  const handleContinue = async () => {
    if (!email.value || isLoading || checkingPasskey) {
      if (!email.value) {
        setEmail({ ...email, errorMessage: 'Please enter your email address' });
      }
      return;
    }

    localStorage.setItem('last_login_email', String(email.value));

    setCheckingPasskey(true);
    const emailHasPasskey = await checkPasskeyForEmail();
    setHasPasskey(emailHasPasskey);

    if (emailHasPasskey) {
      goToStep('passkey', 'forward');
    } else {
      goToStep('password', 'forward');
    }
  };

  // Go back to email step
  const handleBack = () => {
    goToStep('email', 'backward');
    setPassword({ value: '' });
    setPasskeyError(null);
  };

  // Switch to password from passkey step
  const handleUsePassword = () => {
    goToStep('password', 'forward');
    setPasskeyError(null);
  };

  const submit = () => {
    if (isLoading) {
      return;
    }

    setIsLoading(true);
    fetch_post(config.signInUrl, {
      email: email.value,
      password: password.value,
      timezoneOffset: timezoneOffset,
    }).then(res => res.json()).then(data => {
      if (!data.success) {
        Object.keys(data.errorMessages).forEach(field => {
          let message = data.errorMessages[field][Object.keys(data.errorMessages[field])[0]];
          switch (field) {
            case 'email':
              setEmail({ ...email, errorMessage: message });
              break;
            case 'password':
              setPassword({ ...password, errorMessage: message });
              break;
          }
        });
      } else {
        // SECURITY: Check for TFA first before passkey enrollment
        // Backend will NOT return passkeyEnrollmentAvailable when TFA is enabled
        if (data.tfaRequired) {
          // Use inline TFA verification step
          goToStep('tfa', 'forward');
          return;
        }

        // Check if passkey enrollment is available (only when TFA is NOT required)
        if (data.passkeyEnrollmentAvailable && passkeySupported && config.passkeyRegisterOptionsUrl) {
          goToStep('passkeyEnrollment', 'forward');
          return;
        }

        if (config.redirect) {
          let redirectUrl = config.redirectUrl;
          if (data.redirectUrl) {
            redirectUrl = data.redirectUrl;
          }
          window.location.href = redirectUrl;
        } else {
          if (onLoginSuccess) {
            onLoginSuccess();
          }
        }
      }
    }).finally(() => {
      setIsLoading(false);
    });
  };

  const handleKeepMeLoggedIn = (isChecked: boolean) => {
    setKeepMeLoggedIn({ value: '1', isChecked: isChecked });
    setIsLoading(true);
    fetch_post(config.keepLoggedInUrl, {
      keepLoggedIn: isChecked,
    }).then(() => {
      setIsLoading(false);
    });
  };

  const handlePasskeySignIn = async () => {
    if (!config.passkeyAuthOptionsUrl || !config.passkeyAuthVerifyUrl) return;

    setPasskeyLoading(true);
    setPasskeyError(null);

    try {
      // Get authentication options with email
      const optionsResponse = await fetch_post(config.passkeyAuthOptionsUrl, {
        email: email.value,
      });
      const optionsData = await optionsResponse.json();

      if (!optionsData.success) {
        throw new Error(optionsData.error || 'Failed to get authentication options');
      }

      // Authenticate with passkey
      const credentialData = await authenticateWithPasskey(optionsData.options);
      await handlePasskeyAuthSuccess(credentialData);
    } catch (err: any) {
      setPasskeyError(err.message || 'Passkey authentication failed');
    } finally {
      setPasskeyLoading(false);
    }
  };

  const handlePasskeyAuthSuccess = async (credentialData: any) => {
    if (!config.passkeyAuthVerifyUrl) return;

    // Verify authentication with server
    const verifyResponse = await fetch_post(config.passkeyAuthVerifyUrl, credentialData);
    const data = await verifyResponse.json();

    if (!data.success) {
      throw new Error(data.error || 'Authentication verification failed');
    }

    // Passkey authentication bypasses 2FA (passkey is already multi-factor)
    // Backend always returns tfaRequired: false for passkey auth
    if (config.redirect) {
      let redirectUrl = config.redirectUrl;
      if (data.redirectUrl) {
        redirectUrl = data.redirectUrl;
      }
      window.location.href = redirectUrl;
    } else {
      if (onLoginSuccess) {
        onLoginSuccess();
      }
    }
  };

  const handleLoginSuccess = (data: any) => {
    if (config.redirect) {
      let redirectUrl = config.redirectUrl;
      if (data.redirectUrl) {
        redirectUrl = data.redirectUrl;
      }
      window.location.href = redirectUrl;
    } else {
      if (onLoginSuccess) {
        onLoginSuccess();
      }
    }
  };

  const handleTfaVerification = () => {
    const codeValue = tfaCode.value.toString();
    if (!codeValue || codeValue.length < 6) {
      setTfaCode({ ...tfaCode, errorMessage: translations.tfaEnterCode || 'Please enter the 6-digit code' });
      return;
    }

    if (!config.tfaVerifyUrl) return;

    setIsLoading(true);
    fetch_post(config.tfaVerifyUrl, { code: tfaCode.value })
      .then(res => res.json())
      .then((data: { success: boolean, redirectUrl?: string, passkeyEnrollmentAvailable?: boolean, error?: string, tfaRequired?: boolean }) => {
        if (data.success) {
          if (data.tfaRequired) {
            // New behavior: switch to inline TFA step
            goToStep('tfa', 'forward');
          } else if (data.passkeyEnrollmentAvailable && isWebAuthnSupported() && config.passkeyRegisterOptionsUrl) {
            // Check if user should enroll passkey
            goToStep('passkeyEnrollment', 'forward');
          } else {
            handleLoginSuccess(data);
          }
        } else {
          setTfaCode({ ...tfaCode, errorMessage: data.error || 'Verification failed' });
        }
      })
      .catch(() => {
        setTfaCode({ ...tfaCode, errorMessage: 'An error occurred' });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handlePasskeyEnrollment = async () => {
    if (!config.passkeyRegisterOptionsUrl || !config.passkeyRegisterVerifyUrl) return;

    setEnrollmentLoading(true);
    setPasskeyError(null);

    try {
      // Get registration options
      const optionsResponse = await fetch_post(config.passkeyRegisterOptionsUrl, {});
      const optionsData = await optionsResponse.json();

      if (!optionsData.success) {
        throw new Error(optionsData.error || 'Failed to get registration options');
      }

      // Register passkey
      const credentialData = await registerPasskey(optionsData.options);

      // Verify registration with server
      const verifyResponse = await fetch_post(config.passkeyRegisterVerifyUrl, credentialData);
      const verifyData = await verifyResponse.json();

      if (!verifyData.success) {
        throw new Error(verifyData.error || 'Failed to register passkey');
      }

      // Success - redirect
      completeLogin();
    } catch (err: any) {
      setPasskeyError(err.message || 'Passkey registration failed');
    } finally {
      setEnrollmentLoading(false);
    }
  };

  const completeLogin = () => {
    if (config.redirect) {
      window.location.href = config.redirectUrl || '/';
    } else {
      if (onLoginSuccess) {
        onLoginSuccess();
      }
    }
  };

  const skipEnrollment = () => {
    completeLogin();
  };

  // Get animation class based on direction
  const getSlideClass = () => {
    if (!previousStep) return '';
    return slideDirection === 'forward' ? 'slide-enter' : 'slide-enter-reverse';
  };

  // Render email step (first step)
  const renderEmailStep = () => (
    <div className={`slide-step ${previousStep !== null ? getSlideClass() : ''}`}>
      <div className="col s12">
        <h4 className="use-account-heading">{translations.useYourAccount || 'Use your Gender-API.com Account'}</h4>
      </div>
      <div className="col s12">
        <EmailInput
          state={email}
          label={translations.yourEmailAddress}
          onChange={(value) => setEmail({ value })}
        />
      </div>

      <div className="col s12">
        <div className="separator-10" />
        <ButtonBig
          onclick={handleContinue}
          label={translations.continue || 'Continue'}
          disabled={checkingPasskey}
          classNames="passkey-button passkey-button-primary"
        />
        {checkingPasskey && (
          <div className="loading-overlay">
            <LoaderSpinner />
          </div>
        )}
      </div>

      <div className="col s12">
        <div className="separator-10" />
        <CheckBox
          state={keepMeLoggedIn}
          label={translations.keepLoggedInCheckbox}
          onChange={(value, isChecked) => handleKeepMeLoggedIn(isChecked)}
        />
      </div>

      <div className="col s12">
        <div className="divider-text">{translations.or}</div>
        <div className="center">
          <a
            target={config.popupGoogle ? '_blank' : ''}
            href={config.googleAuthUrl}
            onClick={() => setGoogleSignInDisabled(true)}
            className={'googleSignIn'}
          >
            <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
            </svg>
            <span>Sign in with Google</span>
          </a>
        </div>
      </div>
    </div>
  );

  // Render passkey step (when passkey is available)
  const renderPasskeyStep = () => (
    <div className={`slide-step ${getSlideClass()}`}>
      <div className="col s12">
        <h4 className="use-account-heading">{translations.chooseLoginMethod || 'Choose Login Method'}</h4>
      </div>
      <div className="col s12">
        <div className="current-email">
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
          </svg>
          {email.value}
        </div>
      </div>

      <div className="col s12">
        <PasskeyButton
          label={translations.signInWithPasskey || 'Sign in with passkey'}
          onClick={handlePasskeySignIn}
          disabled={passkeyLoading || isLoading}
          loading={passkeyLoading}
          variant="primary"
        />
      </div>

      {passkeyError && (
        <div className="col s12">
          <div className="error-message">
            {passkeyError}
          </div>
        </div>
      )}

      <div className="col s12">
        <div className="separator-15" />
        <div className="center">
          <a
            href="#"
            onClick={(e) => { e.preventDefault(); handleUsePassword(); }}
            className="alt-action-link"
          >
            {translations.signInWithPassword || 'Sign in with password'}
          </a>
        </div>
      </div>

      <div className="col s12">
        <div className="separator-15" />
        <div className="center">
          <a
            href="#"
            onClick={(e) => { e.preventDefault(); handleBack(); }}
            className="back-link"
          >
            ← {translations.back || 'Back'}
          </a>
        </div>
      </div>
    </div>
  );

  // Render password step
  const renderPasswordStep = () => (
    <div className={`slide-step ${getSlideClass()}`}>
      <div className="col s12">
        <h4 className="use-account-heading">{translations.signInWithPassword || 'Sign in with password'}</h4>
      </div>
      <div className="col s12">
        <div className="current-email">
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
          </svg>
          {email.value}
        </div>
      </div>

      <div className="col s12">
        <PasswordInput
          onKeyDown={(value, event) => {
            if (event.keyCode === 13) {
              submit();
            }
          }}
          state={password}
          label={translations.yourPassword}
          onChange={(value: string) => setPassword({ value })}
        />
      </div>

      <div className="col s12">
        <div className="separator-10" />
        <ButtonBig onclick={submit} label={translations.login} disabled={isLoading} classNames="passkey-button passkey-button-primary" />
      </div>

      <div className="col s12">
        <div className="separator-10" />
        <div className="center">
          <a
            href={config.passwordForgottenUrl || '#'}
            className="back-link"
          >
            {translations.passwordForgotten || 'Password forgotten?'}
          </a>
        </div>
      </div>

      {hasPasskey && (
        <div className="col s12">
          <div className="separator-15" />
          <div className="center">
            <a
              href="#"
              onClick={(e) => { e.preventDefault(); goToStep('passkey', 'backward'); }}
              className="alt-action-link passkey-link-with-icon"
            >
              <svg className="passkey-link-icon" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C9.243 2 7 4.243 7 7c0 2.197 1.428 4.058 3.4 4.731V14H9v2h1.4v2H9v2h6v-2h-1.4v-2H15v-2h-1.4v-2.269C15.572 11.058 17 9.197 17 7c0-2.757-2.243-5-5-5zm0 2c1.654 0 3 1.346 3 3s-1.346 3-3 3-3-1.346-3-3 1.346-3 3-3z" />
              </svg>
              {translations.signInWithPasskey || 'Sign in with passkey'}
            </a>
          </div>
        </div>
      )}

      <div className="col s12">
        <div className="separator-15" />
        <div className="center">
          <a
            href="#"
            onClick={(e) => { e.preventDefault(); handleBack(); }}
            className="back-link"
          >
            ← {translations.back || 'Back'}
          </a>
        </div>
      </div>
    </div>
  );

  // Render passkey enrollment step (after password login)
  const renderPasskeyEnrollmentStep = () => (
    <div className={`slide-step ${getSlideClass()}`}>
      <div className="col s12">
        <div className="passkey-enrollment-prompt">
          <div className="passkey-icon">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.81 4.47c-.08 0-.16-.02-.23-.06C15.66 3.42 14 3 12.01 3c-1.98 0-3.86.47-5.57 1.41-.24.13-.54.04-.68-.2-.13-.24-.04-.55.2-.68C7.82 2.52 9.86 2 12.01 2c2.13 0 3.99.47 6.03 1.52.25.14.34.44.19.69-.09.16-.26.26-.42.26zM3.5 9.72c-.1 0-.2-.03-.29-.09-.23-.16-.28-.47-.12-.7.99-1.4 2.25-2.5 3.75-3.27C9.98 4.04 14 4.03 17.15 5.65c1.5.77 2.76 1.86 3.75 3.25.16.22.11.54-.12.7-.23.16-.54.11-.7-.12-.9-1.26-2.04-2.25-3.39-2.94-2.87-1.47-6.54-1.47-9.4.01-1.36.7-2.5 1.7-3.4 2.96-.08.14-.23.21-.39.21zm6.25 12.07c-.13 0-.26-.05-.35-.15-.87-.87-1.34-1.43-2.01-2.64-.69-1.23-1.05-2.73-1.05-4.34 0-2.97 2.54-5.39 5.66-5.39s5.66 2.42 5.66 5.39c0 .28-.22.5-.5.5s-.5-.22-.5-.5c0-2.42-2.09-4.39-4.66-4.39-2.57 0-4.66 1.97-4.66 4.39 0 1.44.32 2.77.93 3.85.64 1.15 1.08 1.64 1.85 2.42.19.2.19.51 0 .71-.11.1-.24.15-.37.15zm7.17-1.85c-1.19 0-2.24-.3-3.1-.89-1.49-1.01-2.38-2.65-2.38-4.39 0-.28.22-.5.5-.5s.5.22.5.5c0 1.41.72 2.74 1.94 3.56.71.48 1.54.71 2.54.71.24 0 .64-.03 1.04-.1.27-.05.53.13.58.41.05.27-.13.53-.41.58-.57.11-1.07.12-1.21.12zM14.91 22c-.04 0-.09-.01-.13-.02-1.59-.44-2.63-1.03-3.72-2.1-1.4-1.39-2.17-3.24-2.17-5.22 0-1.62 1.38-2.94 3.08-2.94 1.7 0 3.08 1.32 3.08 2.94 0 1.07.93 1.94 2.08 1.94s2.08-.87 2.08-1.94c0-3.77-3.25-6.83-7.25-6.83-2.84 0-5.44 1.58-6.61 4.03-.39.81-.59 1.76-.59 2.8 0 .78.07 2.01.67 3.61.1.26-.03.55-.29.64-.26.1-.55-.04-.64-.29-.49-1.31-.73-2.61-.73-3.96 0-1.2.23-2.29.68-3.24 1.33-2.79 4.28-4.6 7.51-4.6 4.55 0 8.25 3.51 8.25 7.83 0 1.62-1.38 2.94-3.08 2.94s-3.08-1.32-3.08-2.94c0-1.07-.93-1.94-2.08-1.94s-2.08.87-2.08 1.94c0 1.71.66 3.31 1.87 4.51.95.94 1.86 1.46 3.27 1.85.27.07.42.35.35.61-.05.23-.26.38-.47.38z" />
            </svg>
          </div>
          <h3>Set up faster login</h3>
          <p>
            Use your fingerprint, face, or screen lock to sign in quickly and securely next time.
          </p>
          <div style={{ marginBottom: '10px' }}>
            <ButtonBig
              label="Set up passkey"
              onclick={handlePasskeyEnrollment}
              disabled={enrollmentLoading}
              classNames="btn white-text green btn-flat waves-effect"
            />
          </div>

          {passkeyError && (
            <div className="error-message" style={{ marginTop: '16px' }}>
              {passkeyError}
            </div>
          )}

          <a
            href="#"
            onClick={(e) => { e.preventDefault(); skipEnrollment(); }}
            className="skip-link"
          >
            Skip for now
          </a>
        </div>
      </div>
    </div>
  );

  // Render TFA step
  const renderTfaStep = () => (
    <div className={`slide-step ${getSlideClass()}`}>
      <div className="col s12">
        <h4 className="use-account-heading">{translations.tfaVerification || '2-Step Verification'}</h4>
      </div>
      <div className="col s12">
        <div className="separator-15" />
        <TFACodeInput
          state={tfaCode}
          onChange={(value) => {
            setTfaCode({ value });
          }}
          onComplete={() => {
            // Optional: auto-submit when complete
            // handleTfaVerification();
          }}
        />
        <div className="separator-15" />
        <div className="center font-small" style={{ color: '#666' }}>
          {translations.tfaHelperText || 'Enter the code from your authenticator app'}
        </div>
        <div className="separator-20" />
      </div>

      <div className="col s12">
        <div className="separator-10" />
        <ButtonBig onclick={handleTfaVerification} label={translations.verify || 'Verify'} disabled={isLoading} classNames="passkey-button passkey-button-primary" />
      </div>

      <div className="col s12">
        <div className="separator-15" />
        <div className="center">
          <a
            href="#"
            onClick={(e) => { e.preventDefault(); handleBack(); }}
            className="back-link"
          >
            ← {translations.back || 'Back'}
          </a>
        </div>
      </div>
    </div>
  );

  return (
    <div className="sign-in-form row">
      {/* Container for slides */}
      <div className="slide-container">
        {googleSignInDisabled && (
          <div className="loading-overlay" style={{ position: 'relative', minHeight: '200px' }}>
            <LoaderSpinner />
          </div>
        )}

        {!googleSignInDisabled && currentStep === 'email' && renderEmailStep()}
        {!googleSignInDisabled && currentStep === 'passkey' && renderPasskeyStep()}
        {!googleSignInDisabled && currentStep === 'password' && renderPasswordStep()}
        {!googleSignInDisabled && currentStep === 'tfa' && renderTfaStep()}
        {!googleSignInDisabled && currentStep === 'passkeyEnrollment' && renderPasskeyEnrollmentStep()}
      </div>
    </div>
  );
};


export default SignInForm;
